import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

interface CMSSettings {
  showLatestBlog: boolean;
  showUpcomingEvents: boolean;
  showFeaturedGallery: boolean;
  blogPostCount: number;
  eventCount: number;
  recentShowsDays: number;
  // Store settings
  enableStoreSorting: boolean;
  defaultStoreSort:
    | "name"
    | "price-low"
    | "price-high"
    | "newest"
    | "best-selling";
  storeProductsPerPage: number;
  enableStoreFiltering: boolean;
}

interface CMSContextType {
  settings: CMSSettings;
  updateSettings: (newSettings: Partial<CMSSettings>) => void;
  toggleSetting: (key: keyof CMSSettings) => void;
}

type PersistedCMSSettings = Pick<
  CMSSettings,
  "enableStoreSorting" | "defaultStoreSort" | "storeProductsPerPage" | "enableStoreFiltering"
>;

const defaultSettings: CMSSettings = {
  showLatestBlog: true,
  showUpcomingEvents: true, // ✅ Enabled by default
  showFeaturedGallery: true, // ✅ Enabled by default
  blogPostCount: 3,
  eventCount: 3,
  recentShowsDays: 2,
  // Store settings
  enableStoreSorting: true,
  defaultStoreSort: "name",
  storeProductsPerPage: 24,
  enableStoreFiltering: true,
};

const CMSContext = createContext<CMSContextType | undefined>(undefined);

const CMS_STORAGE_KEY = "eruption-cms-settings";

const getPersistedSettings = (settings: Partial<CMSSettings>): PersistedCMSSettings => ({
  enableStoreSorting:
    settings.enableStoreSorting ?? defaultSettings.enableStoreSorting,
  defaultStoreSort: settings.defaultStoreSort ?? defaultSettings.defaultStoreSort,
  storeProductsPerPage:
    settings.storeProductsPerPage ?? defaultSettings.storeProductsPerPage,
  enableStoreFiltering:
    settings.enableStoreFiltering ?? defaultSettings.enableStoreFiltering,
});

export const CMSProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<CMSSettings>(() => {
    if (typeof window === "undefined") {
      return defaultSettings;
    }

    const saved = localStorage.getItem(CMS_STORAGE_KEY);
    if (!saved) {
      return defaultSettings;
    }

    try {
      return {
        ...defaultSettings,
        ...getPersistedSettings(JSON.parse(saved)),
      };
    } catch {
      return defaultSettings;
    }
  });

  const updateSettings = useCallback(
    (newSettings: Partial<CMSSettings>) => {
      const updated = { ...settings, ...newSettings };
      setSettings(updated);

      if (typeof window !== "undefined") {
        localStorage.setItem(
          CMS_STORAGE_KEY,
          JSON.stringify(getPersistedSettings(updated))
        );
      }
    },
    [settings]
  );

  const toggleSetting = useCallback(
    (key: keyof CMSSettings) => {
      if (typeof settings[key] === "boolean") {
        updateSettings({ [key]: !settings[key] });
      }
    },
    [settings, updateSettings]
  );

  const contextValue = useMemo(
    () => ({
      settings,
      updateSettings,
      toggleSetting,
    }),
    [settings, updateSettings, toggleSetting]
  );

  return (
    <CMSContext.Provider value={contextValue}>{children}</CMSContext.Provider>
  );
};

export const useCMS = () => {
  const context = useContext(CMSContext);
  if (context === undefined) {
    throw new Error("useCMS must be used within a CMSProvider");
  }
  return context;
};
